import classNames from "classnames";
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonStyles,
  ButtonTypes,
} from "icr-shared-components/dist";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { Dispatch, bindActionCreators } from "redux";
import { current, globalSettings, logOutLocally } from "../../actions";
import { ReactComponent as LogOut } from "../../assets/icons/log-out.svg";
import { ReactComponent as ProfileIcon } from "../../assets/icons/profile-icon.svg";
import { ReactComponent as EyeOffIcon } from "../../assets/icons/eye-off.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";

import { RootState } from "../../reducers";
import Dropdown, { DropdownTypes } from "../Dropdown";

import LanguageSelector from "../LanguageSelector/LanguageSelector";
import styles from "./Header.module.scss";
import { useTranslation } from "react-i18next";
import { DefaultRouteNames, getRouteByLanguage } from "../../routes";
import { i18n } from "../../locale";
import useIsUserAbleToImpersonate from "../../hooks/impersonation/useIsUserAbleToImpersonate";
import { getVersicherungenPageData } from "../../actions/versicherungen";
import { getProfile } from "../../actions/profile";

interface Props {
  children?: any;
  logOutLocally: any;
  currentUser: any;
  current: any;
  configurations: string;
  // actions to reload anonymized pages
  getVersicherungenPageData: any;
  getProfile: any;
  // anonymization
  disableAnonymization?: boolean;
}

const Header = ({
  children,
  logOutLocally,
  currentUser,
  configurations,
  current,
  getVersicherungenPageData,
  getProfile,
  disableAnonymization,
}: Props) => {
  const history = useHistory();
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [headerTopPosition, setHeaderTopPosition] = useState(0);

  const { t: translate } = useTranslation(["profile"]);
  const t = (key: string, defaultValue: string) =>
    translate(key, { defaultValue });

  useEffect(() => {
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const isLoginOrRegisterPage = () => {
    const path = window.location.pathname;
    return (
      path === getRouteByLanguage(DefaultRouteNames.LOGIN) ||
      path === getRouteByLanguage(DefaultRouteNames.LOGINLINK) ||
      path === getRouteByLanguage(DefaultRouteNames.LOGINREMINDER) ||
      path === getRouteByLanguage(DefaultRouteNames.LOGINSMS) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTER) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERNAME) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERSEND) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERSMS) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERCODE) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERFINISH) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERBIRTHDAY) ||
      path === getRouteByLanguage(DefaultRouteNames.REGISTERAHV)
    );
  };

  const loadCurrentUser = () => {
    if (!currentUser && !isLoginOrRegisterPage()) {
      current();
    }
  };

  useEffect(() => {
    loadCurrentUser();
  }, []);

  let previous_scroll_top = null;

  const handleScroll = () => {
    const body_top = document.body.getBoundingClientRect().top;
    if (previous_scroll_top == null) {
      setHeaderTopPosition(0);
    } else if (body_top > previous_scroll_top) {
      // scrolling up
      setIsScrollingDown(false);
      setHeaderTopPosition(0);
    } else {
      // scrolling down
      if (body_top > -80) {
        setIsScrollingDown(true);
        setHeaderTopPosition(body_top);
      } else {
        setHeaderTopPosition(-80);
      }
    }
    previous_scroll_top = body_top;
  };

  const [isAnonymized, setIsAnonymized] = useState(null);

  const logoImage = configurations[i18n?.language]?.logoImage;

  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);

  const isAdminUser = useIsUserAbleToImpersonate();
  const updateAnonymizedPages = () => {
    if (pathname.includes(getRouteByLanguage(DefaultRouteNames.INSURANCE))) {
      getVersicherungenPageData();
    }
    if (pathname.includes(getRouteByLanguage(DefaultRouteNames.PROFILE))) {
      getProfile();
    }
  };

  useEffect(() => {
    if (isAdminUser) {
      if (params.get("anonymize") !== null && isAnonymized === null) {
        setIsAnonymized(params.get("anonymize") === "true");
        updateAnonymizedPages();
        updateUrlParameters();
      }
    }
  }, [params]);

  useEffect(() => {
    if (isAdminUser) {
      if (isAnonymized !== null) {
        params.set("anonymize", isAnonymized.toString());
        updateUrlParameters();
      }
    }
  }, [pathname]);

  const setLock = () => {
    if (isAdminUser) {
      setIsAnonymized(!isAnonymized);

      params.set("anonymize", (!isAnonymized).toString());
      updateUrlParameters();
      updateAnonymizedPages();
    }
  };

  const updateUrlParameters = () => {
    window.history.replaceState(null, "", `${pathname}?${params.toString()}`);
    // Dispara um evento de atualização de URL
    window.dispatchEvent(new Event("popstate"));
  };

  useEffect(() => {
    if (isAdminUser && !isLoginOrRegisterPage()) current();
  }, [isAnonymized]);

  return (
    <div
      className={classNames(styles.header, {
        [styles.isScrollingDown]: isScrollingDown,
      })}
      style={{ top: headerTopPosition }}
    >
      {logoImage == null && (
        <h4 className={styles.headerTitle}>Pensionskasse BVG</h4>
      )}

      {logoImage && (
        <img className={styles.headerLogo} src={logoImage} alt="Header logo" />
      )}

      {children}

      <div className="d-flex">
        {isAdminUser && !disableAnonymization && (
          <div className="mr-24">
            {!isAnonymized && <EyeIcon onClick={setLock} />}
            {isAnonymized && <EyeOffIcon onClick={setLock} />}
          </div>
        )}

        <div style={{ alignContent: "flex-end" }} className="mr-24">
          <LanguageSelector />
        </div>

        <Dropdown
          icon={<ProfileIcon />}
          title={
            (currentUser?.firstname || "") + " " + (currentUser?.lastname || "")
          }
          type={DropdownTypes.Account}
          classNameContent={"user-dropdown-content"}
        >
          {(handleClose) => (
            <div className="p-32">
              {!!currentUser && (
                <h5>
                  {currentUser?.firstname} {currentUser?.lastname}
                </h5>
              )}
              {!!currentUser && (
                <p className="mb-40">{currentUser?.emailAddress}</p>
              )}
              <p
                className="mb-24 cursor-pointer"
                onClick={() => {
                  history.push(getRouteByLanguage(DefaultRouteNames.PROFILE));
                  handleClose();
                }}
              >
                {t("page.title", "Profil")}
              </p>
              <hr className="mb-24" />
              <p
                className="mb-24 cursor-pointer"
                onClick={() => {
                  window.open(
                    configurations[i18n.language]?.conditionsOfUse,
                    "_blank"
                  );
                }}
              >
                {t("termsOfUse.text", "Nutzungsbedingungen")}
              </p>
              <hr className="mb-40" />
              <Button
                style={ButtonStyles.Secondary}
                size={ButtonSizes.Full}
                color={ButtonColors.Error}
                type={ButtonTypes.Standard}
                onClick={() => logOutLocally()}
              >
                {t("button.exit", "Abmelden")}
                <LogOut className="ml-16" />
              </Button>
            </div>
          )}
        </Dropdown>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUser: state.app.currentUser,
  configurations: state.app.configurations,
  disableAnonymization: state.app.disableAnonymization,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  current: bindActionCreators(current, dispatch),
  getVersicherungenPageData: bindActionCreators(
    getVersicherungenPageData,
    dispatch
  ),
  getProfile: bindActionCreators(getProfile, dispatch),
  logOutLocally: bindActionCreators(logOutLocally, dispatch),
  globalSettings: bindActionCreators(globalSettings, dispatch),
});
const connector = connect(mapStateToProps, mapDispatchToProps);
export default connector(Header);
